import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiPaperclip } from 'react-icons/fi';
import Layout from '../../Layout/Layout';
import styles from './EmailInbox.module.css';
import { URL as BASEURL } from '../../AxiosUrlGlobal';
import EmailDetailModal from './EmailDetailModal';

/**
 * EmailInbox - Email Inbox for Super Admin
 * View all emails from all domains with pagination and reply functionality
 */
function EmailInbox() {
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        limit: 20,
        hasPrevPage: false,
        hasNextPage: false
    });

    const [filters, setFilters] = useState({
        search: '',
        domain: '',
        email: ''
    });

    const userId = localStorage.getItem('id');
    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchEmails();
    }, [pagination.currentPage, filters]);

    const fetchEmails = async () => {
        try {
            setLoading(true);
            setError('');

            const queryParams = new URLSearchParams({
                page: pagination.currentPage,
                limit: pagination.limit,
                ...(filters.domain && { domain: filters.domain }),
                ...(filters.email && { email: filters.email }),
                ...(filters.search && { search: filters.search })
            });

            const response = await axios.get(
                `${BASEURL}superadmin/emails/${userId}?${queryParams}`,
                { headers: { token } }
            );

            if (response.data.success) {
                setEmails(response.data.data.emails);
                setPagination(response.data.data.pagination);
            } else {
                setError(response.data.error || 'Failed to load emails');
            }
        } catch (err) {
            console.error('Error fetching emails:', err);
            setError(err.response?.data?.error || 'Failed to load emails');
        } finally {
            setLoading(false);
        }
    };

    const handleEmailClick = async (email) => {
        try {
            // URL encode the emailId to handle special characters and dots
            const emailId = encodeURIComponent(email.id);
            const response = await axios.get(
                `${BASEURL}superadmin/email/${userId}?emailId=${emailId}`,
                { headers: { token } }
            );

            if (response.data.success) {
                setSelectedEmail(response.data.data.email);
                setShowDetailModal(true);
            } else {
                alert('Failed to load email details');
            }
        } catch (err) {
            console.error('Error fetching email details:', err);
            alert('Failed to load email details');
        }
    };

    const handleEmailDelete = (emailId) => {
        // Remove the deleted email from the list
        setEmails(prevEmails => prevEmails.filter(e => e.id !== emailId));
        // Update pagination
        setPagination(prev => ({
            ...prev,
            totalRecords: prev.totalRecords - 1
        }));
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const formatDate = (date) => {
        const d = new Date(date);
        const now = new Date();
        const diff = now - d;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days === 0) {
            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else if (days === 1) {
            return 'Yesterday';
        } else if (days < 7) {
            return `${days} days ago`;
        } else {
            return d.toLocaleDateString();
        }
    };

    const formatSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const getFromDisplay = (from) => {
        if (!from) return 'Unknown';
        if (from.name) {
            return `${from.name} <${from.address}>`;
        }
        return from.address || 'Unknown';
    };

    return (
        <Layout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div>
                        <h1>Email Inbox</h1>
                        <p>View and manage emails from all domains</p>
                    </div>
                </div>

                {/* Filters */}
                <div className={styles.filters}>
                    <input
                        type="text"
                        placeholder="Search emails..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        className={styles.searchInput}
                    />
                    <input
                        type="text"
                        placeholder="Filter by domain..."
                        value={filters.domain}
                        onChange={(e) => handleFilterChange('domain', e.target.value)}
                        className={styles.filterInput}
                    />
                    <input
                        type="text"
                        placeholder="Filter by email account..."
                        value={filters.email}
                        onChange={(e) => handleFilterChange('email', e.target.value)}
                        className={styles.filterInput}
                    />
                </div>

                {error && <div className={styles.error}>{error}</div>}

                {loading ? (
                    <div className={styles.loading}>Loading emails...</div>
                ) : emails.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>No emails found</p>
                        <p className={styles.emptyHint}>
                            {filters.search || filters.domain || filters.email
                                ? 'Try adjusting your filters'
                                : 'No emails in any mailboxes'}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Email List */}
                        <div className={styles.emailList}>
                            {emails.map((email) => (
                                <div
                                    key={email.id}
                                    className={`${styles.emailItem} ${!email.isRead ? styles.unread : ''}`}
                                    onClick={() => handleEmailClick(email)}
                                >
                                    <div className={styles.emailHeader}>
                                        <div className={styles.emailFrom}>
                                            <strong>{getFromDisplay(email.from)}</strong>
                                            {!email.isRead && <span className={styles.unreadBadge}>New</span>}
                                        </div>
                                        <div className={styles.emailMeta}>
                                            <span className={styles.emailDate}>{formatDate(email.date)}</span>
                                            {email.hasAttachments && (
                                                <span className={styles.attachmentIcon}><FiPaperclip /></span>
                                            )}
                                            <span className={styles.emailSize}>{formatSize(email.size)}</span>
                                        </div>
                                    </div>
                                    <div className={styles.emailSubject}>
                                        {email.subject}
                                    </div>
                                    <div className={styles.emailAccount}>
                                        <span className={styles.accountBadge}>{email.accountEmail}</span>
                                        <span className={styles.domainBadge}>{email.accountDomain}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className={styles.pagination}>
                                <button
                                    disabled={!pagination.hasPrevPage}
                                    onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                                >
                                    Previous
                                </button>
                                <span>
                                    Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalRecords} emails)
                                </span>
                                <button
                                    disabled={!pagination.hasNextPage}
                                    onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* Email Detail Modal */}
                {showDetailModal && selectedEmail && (
                    <EmailDetailModal
                        email={selectedEmail}
                        onClose={() => {
                            setShowDetailModal(false);
                            setSelectedEmail(null);
                            // Refresh emails to update read status
                            fetchEmails();
                        }}
                        onDelete={handleEmailDelete}
                        userId={userId}
                        token={token}
                    />
                )}
            </div>
        </Layout>
    );
}

export default EmailInbox;

